import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Booking from "@/pages/Booking";
import Confirmation from "@/pages/Confirmation";
import CheckBooking from "@/pages/CheckBooking";
import PackagesPage from "@/pages/PackagesPage";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import Bookings from "@/admin/Bookings";
import BookingDetail from "@/admin/BookingDetail";
import CalendarPage from "@/admin/CalendarPage";
import Customers from "@/admin/Customers";
import Resources from "@/admin/Resources";
import Branches from "@/admin/Branches";import Payments from "@/admin/Payments";
import Inspections from "@/admin/Inspections";
import SettingsPage from "@/admin/SettingsPage";
import UnitProfile from "@/admin/UnitProfile";
import Reminders from "@/admin/Reminders";
import Users from "@/admin/Users";
import AuditLog from "@/admin/AuditLog";
import DailySummary from "@/admin/DailySummary";
import WeeklySummary from "@/admin/WeeklySummary";
import OnlinePayments from "@/admin/OnlinePayments";
import PaymentProofs from "@/admin/PaymentProofs";
import Returns from "@/admin/Returns";
import Scan from "@/admin/Scan";
import Labels from "@/admin/Labels";
import Terms from "@/pages/Terms";
import MemberResetPassword from "@/pages/MemberResetPassword";
import GoLive from "@/admin/GoLive";
import SandboxPay from "@/admin/SandboxPay";
import PaymentResult from "@/pages/PaymentResult";
import MarginReport from "@/admin/MarginReport";
import SourceReport from "@/admin/SourceReport";
import SiteMedia from "@/admin/SiteMedia";
import Capture from "@/admin/Capture";
import WhatsAppHub from "@/admin/WhatsAppHub";
import AiAssistant from "@/admin/AiAssistant";
import MemberLogin from "@/pages/MemberLogin";
import MemberDashboard from "@/pages/MemberDashboard";
import MemberBookings from "@/pages/MemberBookings";
import MemberAddresses from "@/pages/MemberAddresses";
import MemberProfile from "@/pages/MemberProfile";
import { MemberProvider } from "@/lib/member";
import { RoleGuard } from "@/admin/RoleGuard";
import NewBooking from "@/admin/NewBooking";
import PartnerHub from "@/admin/PartnerHub";
import PartnerCommissions from "@/admin/PartnerCommissions";
import Payouts from "@/admin/Payouts";
import HubPerformance from "@/admin/HubPerformance";
import DepositReport from "@/admin/DepositReport";
import OrderReceipts from "@/admin/OrderReceipts";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-ink-soft">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/book" element={<Booking />} />
      <Route path="/booking/:code" element={<Confirmation />} />
      <Route path="/check-booking" element={<CheckBooking />} />
      <Route path="/packages" element={<PackagesPage />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/syarat-dan-ketentuan" element={<Terms />} />
      <Route path="/member/reset-password" element={<MemberResetPassword />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/member/login" element={<MemberLogin />} />
      <Route path="/payment/finish" element={<PaymentResult kind="finish" />} />
      <Route path="/payment/unfinish" element={<PaymentResult kind="unfinish" />} />
      <Route path="/payment/error" element={<PaymentResult kind="error" />} />      <Route path="/member" element={<MemberDashboard />} />
      <Route path="/member/bookings" element={<MemberBookings />} />
      <Route path="/member/addresses" element={<MemberAddresses />} />
      <Route path="/member/profile" element={<MemberProfile />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
        <Route index element={<RoleGuard area=""><Dashboard /></RoleGuard>} />
        <Route path="partner" element={<RoleGuard area="partner"><PartnerHub /></RoleGuard>} />
        <Route path="partner-commissions" element={<RoleGuard area="partner-commissions"><PartnerCommissions /></RoleGuard>} />
        <Route path="payouts" element={<RoleGuard area="payouts"><Payouts /></RoleGuard>} />
        <Route path="reports/hub-performance" element={<RoleGuard area="reports/hub-performance"><HubPerformance /></RoleGuard>} />
        <Route path="reports/deposits" element={<RoleGuard area="reports/deposits"><DepositReport /></RoleGuard>} />
        <Route path="receipts" element={<RoleGuard area="receipts"><OrderReceipts /></RoleGuard>} />
        <Route path="bookings" element={<RoleGuard area="bookings"><Bookings /></RoleGuard>} />
        <Route path="bookings/new" element={<RoleGuard area="bookings/new"><NewBooking /></RoleGuard>} />
        <Route path="bookings/:id" element={<RoleGuard area="bookings"><BookingDetail /></RoleGuard>} />
        <Route path="calendar" element={<RoleGuard area="calendar"><CalendarPage /></RoleGuard>} />
        <Route path="reminders" element={<RoleGuard area="reminders"><Reminders /></RoleGuard>} />
        <Route path="capture" element={<RoleGuard area="capture"><Capture /></RoleGuard>} />
        <Route path="whatsapp" element={<RoleGuard area="whatsapp"><WhatsAppHub /></RoleGuard>} />
        <Route path="ai" element={<RoleGuard area="ai"><AiAssistant /></RoleGuard>} />
        <Route path="reports/margin" element={<RoleGuard area="reports/margin"><MarginReport /></RoleGuard>} />
        <Route path="reports/sources" element={<RoleGuard area="reports/sources"><SourceReport /></RoleGuard>} />
        <Route path="site-media" element={<RoleGuard area="site-media"><SiteMedia /></RoleGuard>} />
        <Route path="users" element={<RoleGuard area="users"><Users /></RoleGuard>} />
        <Route path="audit-logs" element={<RoleGuard area="audit-logs"><AuditLog /></RoleGuard>} />
        <Route path="summary" element={<RoleGuard area="summary"><DailySummary /></RoleGuard>} />
        <Route path="weekly-summary" element={<RoleGuard area="weekly-summary"><WeeklySummary /></RoleGuard>} />
        <Route path="go-live" element={<RoleGuard area="go-live"><GoLive /></RoleGuard>} />
        <Route path="sandbox-pay" element={<RoleGuard area="sandbox-pay"><SandboxPay /></RoleGuard>} />
        <Route path="customers" element={<RoleGuard area="customers"><Customers /></RoleGuard>} />
        <Route path="units" element={<RoleGuard area="units"><Resources kind="units" /></RoleGuard>} />
        <Route path="branches" element={<RoleGuard area="branches"><Branches /></RoleGuard>} />
        <Route path="units/:id" element={<RoleGuard area="units"><UnitProfile /></RoleGuard>} />
        <Route path="packages" element={<RoleGuard area="packages"><Resources kind="packages" /></RoleGuard>} />
        <Route path="promotions" element={<RoleGuard area="promotions"><Resources kind="promos" /></RoleGuard>} />
        <Route path="delivery-areas" element={<RoleGuard area="delivery-areas"><Resources kind="delivery-areas" /></RoleGuard>} />
        <Route path="addons" element={<RoleGuard area="addons"><Resources kind="addons" /></RoleGuard>} />
        <Route path="payments" element={<RoleGuard area="payments"><Payments /></RoleGuard>} />
        <Route path="online-payments" element={<RoleGuard area="online-payments"><OnlinePayments /></RoleGuard>} />
        <Route path="payment-proofs" element={<RoleGuard area="payment-proofs"><PaymentProofs /></RoleGuard>} />
        <Route path="returns" element={<RoleGuard area="returns"><Returns /></RoleGuard>} />
        <Route path="scan" element={<RoleGuard area="scan"><Scan /></RoleGuard>} />
        <Route path="labels" element={<RoleGuard area="labels"><Labels /></RoleGuard>} />
        <Route path="inspections" element={<RoleGuard area="inspections"><Inspections /></RoleGuard>} />
        <Route path="settings" element={<RoleGuard area="settings"><SettingsPage /></RoleGuard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <MemberProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </MemberProvider>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
